import { html, Html, View } from 'rune-ts';
import {
  CreatorProductData,
  EditCreatorProductData,
  EditCreatorProductData_TPL,
  EditCreatorProductData_TPL_INTL,
  MODE,
  NewCreatorProductData,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import klass from './CreatorProductEditorModal.module.scss';
import { Modal, ModalProps } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import {
  CreatorProductEditorBase,
  EditCreatorProductEditor,
  EditTplCreatorProductEditor,
  EditTplIntlCreatorProductEditor,
  NewCreatorProductEditor,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/CreatorProductEditor.ts';
import { ChangeModeEvent } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/events.ts';
import { GOODS_TYPE } from '@services/MarppleShop/features/Product/type';
import { BaseForm } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/BaseForm.ts';
import { Confirm } from '@services/MarppleShop/renderApp/components/cells/Confirm/Confirm.ts';
import { EditorSaveClientError } from '@services/MarppleShopStudio/widget/CreatorProduct/api';
import { ValidateFormErrorKeyTypes } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/helper.ts';
import { Locals } from '@services/MarppleShop/shared/type/locals.ts';

interface CreatorProductEditorModalProps {
  webviewapp?: Locals['webviewapp'];
  is_mobile?: boolean;
}

export class CreatorProductEditorModal extends View<CreatorProductEditorModalProps> {
  private modal = new Modal(this.createModalProps());

  protected override template(): Html {
    return html`<div>${this.modal}</div>`;
  }

  private onModalClose() {
    try {
      return Confirm.open({
        title: '상품 등록하기 종료',
        message: '현재 등록 중인 상품을 저장하지 않고\n종료하시겠습니까?',
        whiteSpace: 'pre_line',
        cancelText: '취소',
        confirmText: '종료',
      }).then((confirm) => {
        if (!confirm) throw new EditorSaveClientError([ValidateFormErrorKeyTypes.CLOSE_MODAL]);
      });
    } catch (_error) {}
  }

  private createModalProps(): ModalProps {
    if (this.data.is_mobile) {
      return {
        isOpen: false,
        enable_app_bottom_padding: true,
        body_klass: klass.body,
        full_width: true,
        backdrop_not_close: true,
        size: 'large',
        body: html``,
        onClose: this.onModalClose,
        title_options: {
          title_align: 'center',
        },
      };
    }

    return {
      isOpen: false,
      size: 'free',
      body: html``,
      full_width: true,
      modal_klass: `${klass.root}`,
      full_body_height: true,
      disable_escape_keydown: true,
      backdrop_not_close: true,
      onClose: this.onModalClose,
      title_options: {
        title_align: 'center',
        use_left_blank_space: true,
      },
    };
  }

  private setModal<T extends CreatorProductData>(title: string, body: CreatorProductEditorBase<T>) {
    return this.modal.setTitle(this.data.is_mobile ? undefined : title).setBody(body);
  }

  public openNew(data: NewCreatorProductData) {
    this.modal
      .setTitle(this.data.is_mobile ? undefined : ET('mps2::seller_studio::editor::pbg::new::title'))
      .setBody(new NewCreatorProductEditor(data))
      .redraw()
      .open();
  }

  public openEdit(data: EditCreatorProductData) {
    this.modal
      .setTitle(this.data.is_mobile ? undefined : ET('mps2::seller_studio::editor::pbg::modify::title'))
      .setBody(new EditCreatorProductEditor(data))
      .redraw()
      .open();
  }

  public openEdit_TPL(data: EditCreatorProductData_TPL) {
    this.modal
      .setTitle(this.data.is_mobile ? undefined : ET('mps2::seller_studio::editor::tpl::modify::title'))
      .setBody(new EditTplCreatorProductEditor(data))
      .redraw()
      .open();
  }

  public openEdit_TPL_INTL(data: EditCreatorProductData_TPL_INTL) {
    this.modal
      .setTitle(this.data.is_mobile ? undefined : ET('mps2::seller_studio::editor::tpl::modify::title'))
      .setBody(new EditTplIntlCreatorProductEditor(data))
      .redraw()
      .open();
  }

  public open(data: EditCreatorProductData) {
    switch (data.goods_type_id) {
      case GOODS_TYPE.TPL: {
        const is_oversea_delivery = Boolean(data?.stores_product?._.product._.base_product.is_use_creator_en);

        if (is_oversea_delivery) {
          return this.openEdit_TPL_INTL({ ...data, mode: MODE.EDIT_TPL_INTL });
        }

        return this.openEdit_TPL({ ...data, mode: MODE.EDIT_TPL });
      }
      case GOODS_TYPE.PBG: {
        const is_modify = !!data?.stores_product?.product_id;

        if (is_modify) {
          return this.openEdit({ ...data, mode: MODE.EDIT });
        }

        return this.openNew({ ...data, mode: MODE.NEW });
      }
      default:
        throw new Error('The editor mode has not been implemented.');
    }
  }

  override onRender() {
    this.addEventListener(ChangeModeEvent, (event) => {
      const { mode, data } = event.detail;

      switch (mode) {
        case MODE.EDIT_TPL:
          return this.setModal(
            ET('mps2::seller_studio::editor::tpl::modify::title'),
            new EditTplCreatorProductEditor({ ...data, mode: MODE.EDIT_TPL }),
          ).redrawBody();

        case MODE.EDIT_TPL_INTL:
          return this.setModal(
            ET('mps2::seller_studio::editor::tpl::modify::title'),
            new EditTplIntlCreatorProductEditor({ ...data, mode: MODE.EDIT_TPL_INTL }),
          ).redrawBody();

        default:
          throw new Error('The editor mode has not been implemented.');
      }
    });

    this.addEventListener(BaseForm.EditorFormCloseEvent, () => this.modal.close());
  }
}
